import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/layouts/DashboardLayout';
import { Button } from '@/components/Button';
import { 
  User, 
  Mail, 
  Building2, 
  Calendar, 
  Shield, 
  Edit, 
  Key,
  CheckCircle 
} from 'lucide-react';

export default function ProfilePage() {
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'candidate':
        return 'bg-blue-100 text-blue-800';
      case 'recruiter':
        return 'bg-green-100 text-green-800';
      case 'hiring_manager':
        return 'bg-purple-100 text-purple-800';
      case 'hr_admin':
        return 'bg-orange-100 text-orange-800';
      case 'org_owner':
        return 'bg-primary-100 text-primary-800';
      case 'super_admin':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatRole = (role: string) => {
    return role.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
            <p className="mt-1 text-sm text-gray-600">
              View and manage your account information
            </p>
          </div>
          <Link to="/profile/edit">
            <Button className="flex items-center">
              <Edit className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
          </Link>
        </div>

        {/* Profile Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Header with gradient */}
          <div className="h-32 bg-gradient-to-r from-primary-500 to-blue-600"></div>
          
          {/* Profile Info */}
          <div className="px-6 pb-6">
            <div className="flex items-start -mt-16 mb-6">
              <div className="flex-shrink-0">
                <div className="w-32 h-32 rounded-full border-4 border-white bg-white flex items-center justify-center shadow-lg">
                  {user.avatar ? (
                    <img 
                      src={user.avatar} 
                      alt={`${user.firstName} ${user.lastName}`}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <User className="w-16 h-16 text-gray-400" />
                  )}
                </div>
              </div>
              
              <div className="ml-6 flex-1 pt-16">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {user.firstName} {user.lastName}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {user.email}
                    </p>
                  </div>
                  
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getRoleBadgeColor(user.role)}`}>
                    {formatRole(user.role)}
                  </span>
                </div>
              </div>
            </div>

            {/* Profile Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                  Personal Information
                </h3>
                
                <div className="flex items-start">
                  <Mail className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Email Address</p>
                    <p className="text-sm text-gray-900">{user.email}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <User className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Full Name</p>
                    <p className="text-sm text-gray-900">
                      {user.firstName} {user.lastName}
                    </p>
                  </div>
                </div>

                {user.company && (
                  <div className="flex items-start">
                    <Building2 className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Organization</p>
                      <p className="text-sm text-gray-900">{user.company}</p>
                    </div>
                  </div>
                )}

                {user.department && (
                  <div className="flex items-start">
                    <Building2 className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Department</p>
                      <p className="text-sm text-gray-900">{user.department}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Account Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">
                  Account Information
                </h3>
                
                <div className="flex items-start">
                  <Shield className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Role</p>
                    <p className="text-sm text-gray-900">{formatRole(user.role)}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Calendar className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">Member Since</p>
                    <p className="text-sm text-gray-900">{formatDate(user.createdAt)}</p>
                  </div>
                </div>

                {user.lastLogin && (
                  <div className="flex items-start">
                    <Calendar className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Last Login</p>
                      <p className="text-sm text-gray-900">{formatDate(user.lastLogin)}</p>
                    </div>
                  </div>
                )}

                {user.accountType && (
                  <div className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Account Type</p>
                      <p className="text-sm text-gray-900 capitalize">{user.accountType}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Security Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Security</h3>
              <p className="text-sm text-gray-600">
                Manage your password and security settings
              </p>
            </div>
            <Link to="/profile/edit">
              <Button
                variant="outline"
                className="flex items-center"
              >
                <Key className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            </Link>
          </div>
        </div>

        {/* Account IDs (for debugging/support) */}
        {(user.id || user.orgId || user.tenantId) && (
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Account Identifiers</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              {user.id && (
                <div>
                  <p className="text-gray-500 mb-1">User ID</p>
                  <code className="block bg-white rounded px-2 py-1 text-gray-700 border border-gray-200 break-all">
                    {user.id}
                  </code>
                </div>
              )}
              {user.orgId && (
                <div>
                  <p className="text-gray-500 mb-1">Organization ID</p>
                  <code className="block bg-white rounded px-2 py-1 text-gray-700 border border-gray-200 break-all">
                    {user.orgId}
                  </code>
                </div>
              )}
              {user.tenantId && (
                <div>
                  <p className="text-gray-500 mb-1">Tenant ID</p>
                  <code className="block bg-white rounded px-2 py-1 text-gray-700 border border-gray-200 break-all">
                    {user.tenantId}
                  </code>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
